import React, { useEffect } from "react";
import "../components/SparklingSocial.css";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Heart,
  Monitor,
  Palette,
  Brain,
  Globe,
  Sprout
} from "lucide-react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------ */
/*  Social Commitment Data                                           */
/* ------------------------------------------------------------------ */

const SOCIAL_AXES = [
  {
    icon: <Heart className="h-7 w-7" />,
    title: "Igualdad e inclusión",
    description: "Promovemos entornos justos con igualdad de oportunidades para crecer, crear y liderar.",
    theme: "pink"
  },
  {
    icon: <Monitor className="h-7 w-7" />,
    title: "Educación tecnológica",
    description: "Fomentamos vocaciones STEM acercando la tecnología a las aulas con charlas y talleres.",
    theme: "blue"
  },
  {
    icon: <Palette className="h-7 w-7" />,
    title: "Cultura y creatividad",
    description: "Apoyamos la creatividad digital y videojuegos con impacto que promueven el pensamiento crítico.",
    theme: "purple"
  },
  {
    icon: <Brain className="h-7 w-7" />,
    title: "Salud y bienestar",
    description: "Soluciones para rehabilitación, envejecimiento activo y deporte adaptado. La salud se cuida jugando.",
    theme: "pink-alt"
  },
  {
    icon: <Globe className="h-7 w-7" />,
    title: "Sostenibilidad e innovación",
    description: "Impulsamos innovación responsable y ética, integrando prácticas sostenibles y eficiencia energética.",
    theme: "blue-alt"
  },
  {
    icon: <Sprout className="h-7 w-7" />,
    title: "Agricultura y territorio",
    description: "Acercamos la tecnología al campo para fortalecer el tejido agroalimentario y apoyar al mundo rural.",
    theme: "purple-alt"
  }
];

const SparklingSocial = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out-cubic',
      disable: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="sparkling-social-page">
      {/* Header Section */}
      <header className="sparkling-social-header">
        <div className="sparkling-inner-header" data-aos="fade-up">
          <div className="sparkling-hero-card">
            <h2 className="about-main-title">
              El lado más humano de la tecnología
            </h2>
            <p className="sparkling-hero-subtitle">
              Unimos tecnología, igualdad y sostenibilidad para construir un futuro más inclusivo, consciente y sostenible.
            </p>
            <div
              className="about-buttons"
              style={{ justifyContent: "flex-start", marginTop: "30px" }}
            >
              <Link
                to="/contacto"
                className="sparkling-btn btn-purple-pink font-bold"
              >
                <div className="sparkling-btn-border" />
                <div className="sparkling-btn-content">Saber más</div>
              </Link>
            </div>
          </div>

          <div className="sparkling-hero-image" data-aos="zoom-in">
            <img
              src="/videos/Sparki_leyendo.gif"
              alt="Sparki leyendo"
              className="sparkling-hero-gif"
            />
          </div>
        </div>

        {/* TOP WAVES - Exact 4-layer structure */}
        <div className="sparkling-waves-wrapper">
          <svg className="waves" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
            <defs>
              <path id="gentle-wave-vibrant" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
            </defs>
            <g className="parallax">
              <use xlinkHref="#gentle-wave-vibrant" x="48" y="0" fill="rgba(59, 130, 246, 0.35)" /> {/* Azul Vibrante */}
              <use xlinkHref="#gentle-wave-vibrant" x="48" y="3" fill="rgba(236, 72, 153, 0.35)" /> {/* Rosa Vibrante */}
              <use xlinkHref="#gentle-wave-vibrant" x="48" y="5" fill="rgba(168, 85, 241, 0.35)" /> {/* Morado Vibrante */}
            </g>
          </svg>
        </div>
      </header>

      <div className="sparkling-social-main">
        {/* Vision Section */}
        <section className="sparkling-vision-section">
          <div className="sparkling-vision-container" data-aos="fade-up">
            <div className="sparkling-vision-card">
              <p className="vision-main-text">
                Sparkling Social es el corazón de nuestra iniciativa: <strong>tecnología con propósito</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* BOTTOM WAVES - PERFECT 180º MIRROR - NO GAPS */}
        <div className="sparkling-waves-wrapper flipped">
          <svg className="waves" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
            <g className="parallax">
              <use xlinkHref="#gentle-wave-vibrant" x="48" y="0" fill="rgba(59, 130, 246, 0.35)" /> {/* Azul Vibrante */}
              <use xlinkHref="#gentle-wave-vibrant" x="48" y="3" fill="rgba(236, 72, 153, 0.35)" /> {/* Rosa Vibrante */}
              <use xlinkHref="#gentle-wave-vibrant" x="48" y="5" fill="rgba(168, 85, 241, 0.35)" /> {/* Morado Vibrante */}
            </g>
          </svg>
        </div>

        {/* Commitment Grid */}
        <section className="social-commitment-refinement">
          <div className="social-commitment-header-ref" data-aos="fade-down">
            <h2 className="about-main-title">
              El progreso solo tiene sentido cuando es compartido
            </h2>
            <p className="social-axes-subtitle-ref">
              Creemos en una tecnología con propósito que mejora vidas y reduce brechas a través de seis ejes fundamentales.
            </p>
          </div>

          <div className="social-commitment-grid-ref">
            {SOCIAL_AXES.map((axis, index) => (
              <div 
                key={index} 
                className={`social-card-ref theme-${axis.theme}`}
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div className="social-card-icon-circle">
                  {axis.icon}
                </div>
                <div className="social-card-content-ref">
                  <h3 className="social-card-title-ref">{axis.title}</h3>
                  <p className="social-card-desc-ref">{axis.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="social-commitment-refinement social-commitment-refinement--certs" style={{ marginTop: "-100px" }}>
        <div className="social-commitment-header-ref" data-aos="fade-down">
            <h2 className="about-main-title">
              Certificados de igualdad y sostenibilidad
            </h2>
            <p className="social-axes-subtitle-ref">
              Nuestros compromisos de sostenibilidad e impacto social.
            </p>
          </div>
        </section>

        <div className="certificados-sostenibilidad" style={{ marginTop: "-110px" }}>

          {/* Card: Igualdad */}
          <div className="cert-card cert-card--equality" data-aos="fade-up" data-aos-delay="0">
            <div className="cert-card__header">
              <span className="cert-card__badge cert-card__badge--purple">Igualdad</span>
              <h3 className="cert-card__title">Certificados de Igualdad</h3>
              <p className="cert-card__desc">
                Reconocimientos que avalan nuestro compromiso con la igualdad de género y la inclusión en el entorno empresarial.
              </p>
            </div>
            <div className="cert-card__logos">
              <div className="cert-logo-item">
                <img
                  src="/img/certificados/weconnect.png"
                  alt="WeConnect International"
                  className="cert-logo cert-logo--weconnect"
                />
                <span className="cert-logo-label">WeConnect International</span>
              </div>
              <div className="cert-logo-item">
                <img
                  src="/img/certificados/women-owned-logo.png"
                  alt="Women Owned"
                  className="cert-logo cert-logo--women"
                />
                <span className="cert-logo-label">Women Owned</span>
              </div>
            </div>
          </div>

          {/* Card: Sostenibilidad */}
          <div className="cert-card cert-card--sustainability" data-aos="fade-up" data-aos-delay="100">
            <div className="cert-card__header">
              <span className="cert-card__badge cert-card__badge--green">Sostenibilidad</span>
              <h3 className="cert-card__title">Certificado Biosphere</h3>
              <p className="cert-card__desc">
                Adheridas al sistema de certificación Biosphere, comprometidas con los Objetivos de Desarrollo Sostenible de la ONU.
              </p>
            </div>
            <div className="cert-card__logos cert-card__logos--biosphere">
              <div className="cert-logo-item">
                <img
                  src="/img/certificados/biosphere_committed-png.png"
                  alt="Biosphere Committed"
                  className="cert-logo cert-logo--biosphere"
                />
                <span className="cert-logo-label">Biosphere Committed</span>
              </div>
              <div className="cert-logo-item cert-logo-item--qr">
                <img
                  src="/img/certificados/compromiso_qr_perfil_2026_es-png.png"
                  alt="QR Compromiso Biosphere 2026"
                  className="cert-logo cert-logo--qr"
                />
                <span className="cert-logo-label">Escanea para verificar</span>
              </div>
            </div>
          </div>

        </div>

        {/* Final CTA */}
        <section className="sparkling-final-cta">
          <div className="final-cta-content" data-aos="fade-up">
            <div className="final-cta-text">
              <h2 className="about-main-title">Impulsamos el cambio que queremos ver</h2>
              <p className="final-cta-desc">
                Conoce más sobre nuestras acciones o solicita nuestros compromisos de impacto social y sostenibilidad. Estamos aquí para construir el futuro juntos.
              </p>
            </div>
            
            <div className="final-cta-action">
              <Link
                to="/contacto"
                className="sparkling-btn btn-purple-pink font-bold"
              >
                <div className="sparkling-btn-border" />
                <div className="sparkling-btn-content">Contáctanos</div>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SparklingSocial;
